import React, { useState } from 'react';
import { SHeaderSortable, SPagination } from '@avalara/skylab-sdk/react';
import _ from 'lodash';
import LANGUAGES_CONTROLLER from '../../controllers/languages';
import { Link } from 'react-router-dom';
import GenericFilterPanel from '../common/GenericFilterPanel';
import { userIdentityLinkTo } from '../../utils/userIdentityRoutes';

const ALL_COMPANIES_GRANT = '*';
const MAX_COMPANIES_COUNT = Number.MAX_VALUE;

const CompanyPermissionsTable = (props) => {

    const [sortingConfig, setSortingConfig] = useState({ columnId: "firstName", order: "asc" });
    const [pagingConfig, setPagingConfig] = useState({ startIndex: 0, endIndex: 9, rowsPerPage: 10 });
    const [filteringConfig, setFilteringConfig] = useState({ companies: undefined, roles: undefined, userName: undefined });
    const [renderedUsersCount, setRenderedUsersCount] = useState(props.users?.length);

    const getCompaniesCount = (user) => {
        if (!user.companyGrants) {
            return 0;
        }
        if (user.companyGrants.includes(ALL_COMPANIES_GRANT)) {
            return MAX_COMPANIES_COUNT;
        }
        return user.companyGrants.length;
    }

    const getDisplayCompanyValue = (user) => {
        if (user.companyGrants.includes(ALL_COMPANIES_GRANT)) {
            return LANGUAGES_CONTROLLER.format(props.languages, "CompanyPermissions_AllCompanies");
        }
        if (user.companiesCount === 1 && props.companiesLookup) {
            return props.companiesLookup[user.companyGrants[0]];
        }
        return LANGUAGES_CONTROLLER.format(props.languages, "CompanyPermissions_Companies", [user.companiesCount]);
    }

    props?.users?.map(user => {
        user.companiesCount = getCompaniesCount(user);
        user.displayCompanyValue = getDisplayCompanyValue(user);
    });

    const onSort = (e) => {
        const columnId = e.detail.id;
        const order = (e.detail.ascending) ? "asc" : "desc";
        if (["id", "firstName", "displayCompanyValue", "role"].includes(columnId)) {
            setSortingConfig({ columnId: columnId, order: order });
        }
    }

    const onPaginate = (e) => {
        const startIndex = e.detail.startIndex;
        const endIndex = e.detail.endIndex;
        const rowsPerPage = e.detail.rowsPerPage;
        setPagingConfig({ startIndex: startIndex, endIndex: endIndex, rowsPerPage: rowsPerPage });
    }

    const getFilteredUsers = () => {
        const filterByCompany = (users) => {
            if (!filteringConfig.companies?.length) {
                return users;
            }
            return users.filter((user) => {
                return user.companyGrants.includes(ALL_COMPANIES_GRANT) || _.intersection(user.companyGrants, filteringConfig.companies).length > 0;
            });
        }

        const filterByRole = (users) => {
            if (!filteringConfig.roles?.length) {
                return users;
            }
            return users.filter((user) => filteringConfig.roles.includes(user.role));
        }

        const filterByUserName = (users) => {
            if (!filteringConfig.userName) {
                return users;
            }
            const searchString = filteringConfig.userName.toLowerCase();
            return users.filter((user) => {
                const fullName = `${user.firstName || ''} ${user.lastName || ''}`.trim();
                return fullName.toLowerCase().includes(searchString);
            });
        }

        if (!props?.users) {
            return [];
        }

        const byCompany = filterByCompany(props.users);
        const byRole = filterByRole(byCompany);
        return filterByUserName(byRole);
    }

    const generateSortedPage = (users) => {
        if (users?.length == 0) {
            return null;
        }

        let sortedUsers = users;
        if (users.length != renderedUsersCount) {
            setPagingConfig({ startIndex: 0, endIndex: 9, rowsPerPage: 10 });
            setRenderedUsersCount(users.length);
        }

        if (sortingConfig.columnId) {
            if (sortingConfig.columnId === "displayCompanyValue") {
                sortedUsers = _.orderBy(users, ["companiesCount", sortingConfig.columnId], [sortingConfig.order, sortingConfig.order])
            }
            else {
                sortedUsers = _.orderBy(users, [sortingConfig.columnId], [sortingConfig.order])
            }
        }

        const currentPage = sortedUsers.slice(pagingConfig.startIndex, pagingConfig.endIndex + 1);
        return currentPage.map(user => {
            const linkToUrl = userIdentityLinkTo('/ViewCompanyPermissions', user.id);
            return (
                <tr key={user.id}>
                    <td className="twenty-width">
                        <Link className="tertiary bold break-word" to={linkToUrl}>
                            {user.firstName} {user.lastName}
                        </Link>
                    </td>
                    <td className="forty-width">{user.id}</td>
                    <td className="twenty-width">{user.role}</td>
                    <td className="twenty-width">
                        {user.displayCompanyValue}
                    </td>
                </tr>
            );
        });
    }

    const filteredUsers = getFilteredUsers();
    const usersRows = generateSortedPage(filteredUsers);

    return (
        <div>
            <GenericFilterPanel
                open={props.toggleFilterPanel}
                onDismiss={() => props.setToggleFilterPanel(false)}
                allCompanyOptions={props.allCompanyOptions}
                setFilteringConfig={setFilteringConfig}
                filterPrefix="CompanyPermissionsFilter"
                showPermissionsFilter={false}
            />
            <s-table-container data-testid="comp-perm-table" tabindex="0">
                <table>
                    <thead>
                        <tr>
                            <th data-testid="first-name-column" className="pb0 pt4">
                                <SHeaderSortable id="firstName"
                                    text={LANGUAGES_CONTROLLER.format(props.languages, "CompanyPermissionsTable_Name")}
                                    onS-sort={onSort} />
                            </th>
                            <th data-testid="email-column" className="pb0 pt4">
                                <SHeaderSortable id="id"
                                    text={LANGUAGES_CONTROLLER.format(props.languages, "CompanyPermissionsTable_Email")}
                                    onS-sort={onSort} />
                            </th>
                            <th data-testid="role-column" className="pb0 pt4">
                                <SHeaderSortable id="role"
                                    text={LANGUAGES_CONTROLLER.format(props.languages, "CompanyPermissionsTable_Role")}
                                    onS-sort={onSort} />
                            </th>
                            <th data-testid="company-column" className="pb0 pt4">
                                <SHeaderSortable id="displayCompanyValue"
                                    text={LANGUAGES_CONTROLLER.format(props.languages, "CompanyPermissionsTable_Company")}
                                    onS-sort={onSort} />
                            </th>
                        </tr>
                    </thead>
                    <tbody>
                        {usersRows}
                    </tbody>
                </table>
            </s-table-container>
            <SPagination className="mt20 mb170"
                totalRecords={renderedUsersCount}
                startIndex={pagingConfig.startIndex}
                rowsPerPage={pagingConfig.rowsPerPage}
                onS-paginate={onPaginate} />
        </div>
    );
};

export default CompanyPermissionsTable;